import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  MapPin,
  Search,
  ShieldCheck,
  Sparkles,
  Linkedin,
  Building2,
  FileSearch,
} from "lucide-react";
import { useSEO } from "@/lib/useSEO";
import { decideRobots, buildCanonical, buildPageMeta } from "@/lib/seoIndex";
import Breadcrumbs from "@/components/Breadcrumbs";

const FOUNDER_PORTRAIT = "/founder.jpg"; // place a real portrait at /app/frontend/public/founder.jpg
const LINKEDIN_URL = "https://www.linkedin.com/in/david-schindler-1a122a313";

export default function AboutPage() {
  const { t, i18n } = useTranslation();
  const isDE = i18n.language?.startsWith("de");

  const meta = buildPageMeta("about");
  useSEO({
    robots: decideRobots({ pageType: "about" }),
    title: meta.title,
    description: meta.description,
    canonical: buildCanonical("/about"),
  });

  return (
    <div className="bg-[#F6F2E8]" data-testid="about-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12 pt-10">
        <Breadcrumbs crumbs={[{ label: t("about.crumb", { defaultValue: "About" }) }]} />
      </div>

      {/* HERO */}
      <section className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12 pt-10 pb-16 md:pt-14 md:pb-24">
        <p className="eyebrow" data-testid="about-hero-eyebrow">
          {t("about.hero_eyebrow", { defaultValue: "About doing-good.org" })}
        </p>
        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-[#1F2A24] max-w-3xl">
          {t("about.hero_title", {
            defaultValue: "Helping people find meaningful ways to support others across Europe.",
          })}
        </h1>
        <p className="mt-6 text-lg text-[#5F6B61] max-w-2xl leading-relaxed">
          {t("about.hero_subtitle", {
            defaultValue:
              "doing-good.org brings together listed volunteering, donation and pro bono opportunities from organisations across Europe.",
          })}
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/offers"
            className="btn-pill inline-flex items-center gap-2 px-6 py-3 bg-[#5B8C66] text-white font-semibold hover:bg-[#3F6147] transition"
            data-testid="about-cta-explore"
          >
            {t("about.cta_primary", { defaultValue: "Explore opportunities" })}
            <ArrowRight size={16} strokeWidth={2.2} />
          </Link>
          <Link
            to="/cities"
            className="btn-pill inline-flex items-center gap-2 px-6 py-3 border border-[#DED7C7] hover:border-[#5B8C66] text-[#1F2A24] font-semibold transition"
            data-testid="about-cta-cities"
          >
            <MapPin size={16} strokeWidth={2} />
            {t("about.cta_secondary", { defaultValue: "Browse by city" })}
          </Link>
        </div>
      </section>

      {/* SECTION 1 — WHY */}
      <section className="border-t border-[#DED7C7] bg-white" data-testid="about-why">
        <div className="max-w-3xl mx-auto px-4 md:px-8 lg:px-12 py-20 md:py-24">
          <p className="eyebrow">{t("about.why_eyebrow", { defaultValue: "Why we exist" })}</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight text-[#1F2A24]">
            {t("about.why_title", { defaultValue: "Why doing-good.org exists" })}
          </h2>
          <div className="mt-7 space-y-5 text-[17px] text-[#3A463C] leading-[1.75]">
            <p>
              {t("about.why_p1", {
                defaultValue:
                  "People want to make a difference — but finding trustworthy ways to help is harder than it should be. Many impactful NGOs and community initiatives remain invisible, hidden across fragmented local websites and disconnected platforms.",
              })}
            </p>
            <p>
              {t("about.why_p2", {
                defaultValue:
                  "doing-good.org was built to change that: making active social impact opportunities easier to discover and participate in.",
              })}
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 2 — HOW */}
      <section className="border-t border-[#DED7C7]" data-testid="about-how">
        <div className="max-w-5xl mx-auto px-4 md:px-8 lg:px-12 py-20 md:py-24">
          <p className="eyebrow">{t("about.how_eyebrow", { defaultValue: "How it works" })}</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight text-[#1F2A24] max-w-2xl">
            {t("about.how_title", { defaultValue: "Getting involved, made simple." })}
          </h2>
          <p className="mt-5 text-[17px] text-[#5F6B61] leading-relaxed max-w-2xl">
            {t("about.how_intro", {
              defaultValue:
                "We make getting involved simple — by gathering, reviewing and surfacing opportunities so you can find the ones that match you.",
            })}
          </p>

          <ol className="mt-10 grid md:grid-cols-3 gap-5" data-testid="about-how-steps">
            {[
              {
                icon: Building2,
                key: "gather",
                defaultTitle: "Gather",
                defaultBody:
                  "We collect opportunities from NGOs and local initiatives across Europe in one place.",
              },
              {
                icon: FileSearch,
                key: "verify",
                defaultTitle: "Verify",
                defaultBody:
                  "We review each project for legitimacy, accuracy and transparency before publishing.",
              },
              {
                icon: Search,
                key: "discover",
                defaultTitle: "Discover",
                defaultBody:
                  "We make them easy to discover by city, cause or category — so you can find a match for your time and interests.",
              },
            ].map((step, i) => {
              const Icon = step.icon;
              return (
                <li
                  key={step.key}
                  className="card-clay bg-white p-7 flex flex-col"
                  data-testid={`about-how-step-${step.key}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold text-[#5B8C66] tabular-nums">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-[#E0EBD3] text-[#3F6147]">
                      <Icon size={16} strokeWidth={1.8} />
                    </span>
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-[#1F2A24]">
                    {t(`about.how_step_${step.key}_title`, { defaultValue: step.defaultTitle })}
                  </h3>
                  <p className="mt-2 text-sm text-[#5F6B61] leading-relaxed">
                    {t(`about.how_step_${step.key}_body`, { defaultValue: step.defaultBody })}
                  </p>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* SECTION 3 — TRANSPARENCY */}
      <section className="border-t border-[#DED7C7] bg-white" data-testid="about-transparency">
        <div className="max-w-5xl mx-auto px-4 md:px-8 lg:px-12 py-20 md:py-24 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-7">
            <p className="eyebrow">{t("about.trans_eyebrow", { defaultValue: "Transparency" })}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight text-[#1F2A24]">
              {t("about.trans_title", { defaultValue: "Transparency" })}
            </h2>
            <div className="mt-7 space-y-5 text-[17px] text-[#3A463C] leading-[1.75]">
              <p>
                {t("about.trans_p1", {
                  defaultValue:
                    "doing-good.org collects and organizes publicly available information about NGOs and social impact opportunities to make volunteering easier to explore.",
                })}
              </p>
              <p>
                {t("about.trans_p2", {
                  defaultValue:
                    "While we review opportunities before they are published, we are not directly affiliated with every listed organization and cannot guarantee all external websites or projects at all times.",
                })}
              </p>
              <p>
                {t("about.trans_p3", {
                  defaultValue:
                    "Our goal is to create a transparent and trustworthy space where people can discover meaningful ways to get involved.",
                })}
              </p>
            </div>
          </div>

          <ul className="md:col-span-5 space-y-3" data-testid="about-trust-indicators">
            {[
              { key: "verified", icon: ShieldCheck, defaultLabel: "Real, identifiable organisations" },
              { key: "public", icon: FileSearch, defaultLabel: "Public NGO information" },
              { key: "review", icon: Sparkles, defaultLabel: "Transparent review process" },
              { key: "independent", icon: Building2, defaultLabel: "Independent platform" },
            ].map(({ key, icon: Icon, defaultLabel }) => (
              <li
                key={key}
                className="flex items-center gap-3 rounded-2xl border border-[#DED7C7] bg-[#F6F2E8] px-4 py-3"
                data-testid={`about-trust-${key}`}
              >
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-white text-[#5B8C66] shrink-0">
                  <Icon size={16} strokeWidth={1.8} />
                </span>
                <span className="text-sm font-semibold text-[#1F2A24]">
                  {t(`about.trust_${key}`, { defaultValue: defaultLabel })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SECTION 4 — FOUNDER */}
      <section className="border-t border-[#DED7C7]" data-testid="about-founder">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12 py-20 md:py-24">
          <p className="eyebrow">{t("about.founder_eyebrow", { defaultValue: "About the founder" })}</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight text-[#1F2A24]">
            {t("about.founder_title", { defaultValue: "About the founder" })}
          </h2>

          <div className="mt-10 grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-4">
              <FounderPortrait initials="DS" src={FOUNDER_PORTRAIT} />
              <p className="mt-4 text-sm font-bold text-[#1F2A24]">David Schindler</p>
              <p className="text-sm text-[#5F6B61]">
                {t("about.founder_role", { defaultValue: "Founder · Zeitlarn, Germany" })}
              </p>
              <span
                className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#DED7C7] bg-white px-3 py-1 text-[11px] font-bold text-[#3F6147] tracking-wider uppercase"
                data-testid="about-founder-badge"
              >
                <Sparkles size={11} strokeWidth={2.4} />
                {t("about.founder_badge", { defaultValue: "Independent project" })}
              </span>
              <div className="mt-4">
                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-pill inline-flex items-center gap-2 px-4 py-2 border border-[#DED7C7] hover:border-[#5B8C66] text-sm font-semibold text-[#1F2A24] transition"
                  data-testid="about-founder-linkedin"
                >
                  <Linkedin size={14} strokeWidth={2} />
                  {t("about.founder_linkedin", { defaultValue: "LinkedIn" })}
                </a>
              </div>
            </div>

            <div className="md:col-span-8 space-y-5 text-[17px] text-[#3A463C] leading-[1.75]">
              <p>
                {t("about.founder_p1", {
                  defaultValue:
                    "doing-good.org was created by David Schindler with the idea of making social engagement easier to find and more accessible.",
                })}
              </p>
              <p>
                {t("about.founder_p2", {
                  defaultValue:
                    "After noticing how fragmented many volunteering opportunities are, the goal became simple: create one place where people can discover trustworthy projects and organizations without unnecessary complexity.",
                })}
              </p>
              <p>
                {t("about.founder_p3", {
                  defaultValue:
                    "This platform is built independently and continues to grow with a focus on transparency, accessibility, and meaningful impact.",
                })}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quiet closing CTA */}
      <section className="border-t border-[#DED7C7] bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8 lg:px-12 py-16 text-center">
          <p className="text-lg text-[#3A463C] leading-relaxed">
            {t("about.closing", {
              defaultValue: "Have feedback, ideas or an organization you'd like to see listed?",
            })}
          </p>
          <a
            href={`mailto:${isDE ? "info" : "info"}@doing-good.org`}
            className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#5B8C66] hover:underline"
            data-testid="about-contact-link"
          >
            {t("about.closing_cta", { defaultValue: "Get in touch" })}
            <ArrowRight size={14} strokeWidth={2.4} />
          </a>
        </div>
      </section>
    </div>
  );
}

/**
 * Founder portrait with graceful fallback to a calm initial-avatar
 * if no /founder.jpg has been uploaded yet.
 */
function FounderPortrait({ initials, src }) {
  return (
    <div
      className="aspect-square rounded-3xl overflow-hidden border border-[#DED7C7] bg-[#E9F0E0] relative"
      data-testid="about-founder-portrait"
    >
      <img
        src={src}
        alt="David Schindler, founder of doing-good.org"
        className="w-full h-full object-cover"
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <span className="text-6xl font-black text-[#3F6147]/30 tracking-tight">{initials}</span>
      </div>
    </div>
  );
}
